import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SupportCategory } from "../contexts/support-types";
import { useSupport } from "../contexts/support-context";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import IconButton from "@mui/material/IconButton";
import TurndownService from "turndown";
import { adminSupportContentApi } from "../services/api/admin-support-content";
import SupportRichTextEditor from "../components/support-rich-text-editor";
import { Link as RouterLink } from "react-router-dom";

const makeSlug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const normalizeSupportData = (
  data: SupportCategory[] | null | undefined,
): SupportCategory[] =>
  (data || []).map((category, catIndex) => ({
    ...category,
    slug: category.slug || makeSlug(category.name),
    sortOrder: category.sortOrder || catIndex + 1,
    articles: (category.articles || []).map((article, artIndex) => ({
      ...article,
      slug: article.slug || makeSlug(article.title),
      sortOrder: article.sortOrder || artIndex + 1,
      published: article.published ?? true,
      content: cleanHtmlContent(article.content || ""),
    })),
  }));

const loadJsonSupportData = async (): Promise<SupportCategory[]> => {
  const res = await fetch(`/support-data.json?cache=${Date.now()}`);

  if (!res.ok) {
    return [];
  }

  const data = await res.json();

  return normalizeSupportData(data);
};

const turndown = new TurndownService({
  headingStyle: "atx",
  codeBlockStyle: "fenced",
  bulletListMarker: "-",
});

// Ensure <br> becomes Markdown line breaks
turndown.addRule("lineBreaks", {
  filter: ["br"],
  replacement: () => "  \n",
});

function cleanHtmlContent(html: string): string {
  let output = html.trim();

  // Convert old Quill YouTube embeds to TipTap YouTube embeds
  output = output.replace(
    /<iframe[^>]*class=["'][^"']*ql-video[^"']*["'][^>]*src=["']([^"']+)["'][^>]*><\/iframe>/gi,
    (_match, src) => {
      const cleanSrc = src.replace(/&amp;/g, "&");

      return `<div data-youtube-video><iframe src="${cleanSrc}"></iframe></div>`;
    },
  );

  // Remove empty paragraph before lists
  output = output.replace(/<p><br><\/p>\s*(?=<(ul|ol)>)/gi, "");

  // Remove empty paragraph after lists
  output = output.replace(/<\/(ul|ol)>\s*<p><br><\/p>/gi, "</$1>");

  // Collapse multiple <p><br></p> into a single one
  output = output.replace(/(<p><br><\/p>\s*){2,}/gi, "<p><br></p>");

  // Remove old empty spacer paragraphs
  output = output.replace(/<p><br><\/p>/gi, "");

  // Remove leading/trailing empty paragraphs
  output = output.replace(/^(<p><br><\/p>\s*)+/i, "");
  output = output.replace(/(<p><br><\/p>\s*)+$/i, "");

  return output.trim();
}

const copyJsonToClipboard = (data: any) => {
  const json = JSON.stringify(data, null, 2);
  navigator.clipboard.writeText(json).then(
    () => alert("JSON copied to clipboard!"),
    () => alert("Failed to copy JSON."),
  );
};

const SupportAdminPage: React.FC = () => {
  const { setSupportData } = useSupport();
  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [localBackup, setLocalBackup] = useState<SupportCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadSupportContent() {
      try {
        const databaseData = normalizeSupportData(
          await adminSupportContentApi.getAll(),
        );

        if (databaseData.length > 0) {
          setCategories(databaseData);
          setLocalBackup(databaseData);
          setSupportData(databaseData);
          return;
        }

        const jsonData = await loadJsonSupportData();

        setCategories(jsonData);
        setLocalBackup(jsonData);
        setSupportData(jsonData);
      } catch (err) {
        console.error("Failed to load admin support content", err);
      } finally {
        setIsLoading(false);
      }
    }

    loadSupportContent();
  }, [setSupportData]);

  const handleSaveOnly = async () => {
    const cleanedCategories = categories.map((category, catIndex) => ({
      ...category,
      sortOrder: catIndex + 1,
      articles: category.articles.map((article, artIndex) => {
        const html = cleanHtmlContent(article.content || "");

        return {
          ...article,
          sortOrder: artIndex + 1,
          content: html,
        };
      }),
    }));

    try {
      const savedCategories = await Promise.all(
        cleanedCategories.map(async (category) => {
          if (!category.id) {
            const createdCategory = await adminSupportContentApi.createCategory(
              {
                name: category.name,
                slug: category.slug,
                sortOrder: category.sortOrder || 0,
              },
            );

            const createdArticles = await Promise.all(
              category.articles.map(async (article) =>
                adminSupportContentApi.createArticle({
                  supportCategoryId: createdCategory.id,
                  title: article.title,
                  slug: article.slug,
                  content: article.content,
                  sortOrder: article.sortOrder || 0,
                  published: article.published ?? true,
                }),
              ),
            );

            return {
              ...createdCategory,
              articles: createdArticles,
            };
          }

          const categoryId = category.id;

          const savedCategory = await adminSupportContentApi.updateCategory(
            categoryId,
            {
              name: category.name,
              slug: category.slug,
              sortOrder: category.sortOrder || 0,
            },
          );

          const savedArticles = await Promise.all(
            category.articles.map(async (article) => {
              if (!article.id) {
                return article;
              }

              return adminSupportContentApi.updateArticle(article.id, {
                supportCategoryId: categoryId,
                title: article.title,
                slug: article.slug,
                content: article.content,
                sortOrder: article.sortOrder || 0,
                published: article.published ?? true,
              });
            }),
          );

          return {
            ...savedCategory,
            articles: savedArticles,
          };
        }),
      );

      setCategories(savedCategories);
      setLocalBackup(savedCategories);
      setSupportData(savedCategories);

      alert("Support content saved.");
    } catch (err) {
      console.error("Failed to save support content", err);
      alert("Failed to save support content.");
    }
  };

  const handleSaveAndDownload = () => {
    const cleanedCategories = categories.map((category) => ({
      ...category,
      articles: category.articles.map((article) => {
        const markdown = turndown
          .turndown(article.content)
          .replace(/\n{3,}/g, "\n\n")
          .replace(/\n+\-/g, "\n-")
          .replace(/\n+\d+\./g, "\n1.");

        return {
          ...article,
          content: markdown.trim(),
        };
      }),
    }));

    const dataStr = JSON.stringify(cleanedCategories, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "support-data.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const moveArticle = (
    catIndex: number,
    artIndex: number,
    direction: -1 | 1,
  ) => {
    const updated = [...categories];
    const articles = updated[catIndex].articles;
    const newIndex = artIndex + direction;

    if (newIndex < 0 || newIndex >= articles.length) return;

    [articles[artIndex], articles[newIndex]] = [
      articles[newIndex],
      articles[artIndex],
    ];
    setCategories(updated);
  };

  const newCategory = {
    id: crypto.randomUUID?.() ?? Math.random().toString(36).substring(2),
    name: "New Category",
    slug: "new-category",
    articles: [],
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>Loading support data...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4">Support Admin</Typography>

        <Button component={RouterLink} to="/admin" variant="outlined">
          Admin Home
        </Button>
      </Box>

      <Box sx={{ mt: 4, px: 3 }}>
        <Button variant="contained" onClick={handleSaveOnly} sx={{ mr: 2 }}>
          Save
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => copyJsonToClipboard(categories)}
        >
          Copy JSON
        </Button>
        <Button variant="outlined" onClick={handleSaveAndDownload}>
          Save and Download
        </Button>
      </Box>

      <Button
        variant="outlined"
        onClick={async () => {
          try {
            const newCategory = await adminSupportContentApi.createCategory({
              name: "New Category",
              slug: `new-category-${Date.now()}`,
              sortOrder: categories.length + 1,
            });

            setCategories([
              ...categories,
              {
                ...newCategory,
                articles: newCategory.articles || [],
              },
            ]);
          } catch (err) {
            console.error("Failed to create support category", err);
            alert("Failed to create support category.");
          }
        }}
        sx={{ mt: 4 }}
      >
        Add New Category
      </Button>

      {[...categories].map((category, catIndex) => (
        <Accordion key={`${category.name}-${catIndex}`} sx={{ mt: 4 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Box
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                sx={{ flex: 1, mr: 2 }}
              >
                <TextField
                  label="Category Name"
                  value={category.name}
                  onChange={(e) => {
                    const updated = [...categories];
                    updated[catIndex].name = e.target.value;
                    setCategories(updated);
                  }}
                  fullWidth
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", mr: 2 }}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    if (catIndex > 0) {
                      const updated = [...categories];
                      [updated[catIndex - 1], updated[catIndex]] = [
                        updated[catIndex],
                        updated[catIndex - 1],
                      ];
                      setCategories(updated);
                    }
                  }}
                  size="small"
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    if (catIndex < categories.length - 1) {
                      const updated = [...categories];
                      [updated[catIndex + 1], updated[catIndex]] = [
                        updated[catIndex],
                        updated[catIndex + 1],
                      ];
                      setCategories(updated);
                    }
                  }}
                  size="small"
                >
                  <ArrowDownwardIcon />
                </IconButton>
              </Box>

              <Button
                variant="outlined"
                color="error"
                onClick={async (e) => {
                  e.stopPropagation();

                  if (
                    !window.confirm(
                      "Are you sure you want to delete this category?",
                    )
                  ) {
                    return;
                  }

                  try {
                    if (category.id) {
                      await adminSupportContentApi.archiveCategory(category.id);

                      setCategories(
                        categories.filter((item) => item.id !== category.id),
                      );
                    } else {
                      const updated = [...categories];
                      updated.splice(catIndex, 1);
                      setCategories(updated);
                    }
                  } catch (err) {
                    console.error("Failed to delete support category", err);
                    alert("Failed to delete support category.");
                  }
                }}
              >
                🗑️ Delete
              </Button>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            {category.articles.map((article, artIndex) => (
              <Accordion key={artIndex} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flexGrow: 1 }}>
                    {article.title || "Untitled Article"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      ml: 2,
                      mr: 6,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (artIndex > 0) {
                          const updated = [...categories];
                          const articles = updated[catIndex].articles;
                          [articles[artIndex - 1], articles[artIndex]] = [
                            articles[artIndex],
                            articles[artIndex - 1],
                          ];
                          setCategories(updated);
                        }
                      }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (artIndex < category.articles.length - 1) {
                          const updated = [...categories];
                          const articles = updated[catIndex].articles;
                          [articles[artIndex + 1], articles[artIndex]] = [
                            articles[artIndex],
                            articles[artIndex + 1],
                          ];
                          setCategories(updated);
                        }
                      }}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <TextField
                    fullWidth
                    label="Title"
                    value={article.title}
                    onChange={(e) => {
                      const updated = [...categories];
                      updated[catIndex].articles[artIndex].title =
                        e.target.value;
                      updated[catIndex].articles[artIndex].slug = e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)/g, "");
                      setCategories(updated);
                    }}
                    sx={{ mb: 2 }}
                  />

                  <SupportRichTextEditor
                    value={article.content || ""}
                    onChange={(content) => {
                      const updated = [...categories];
                      updated[catIndex].articles[artIndex].content = content;
                      setCategories(updated);
                    }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={async () => {
                      if (!window.confirm("Delete this article?")) {
                        return;
                      }

                      try {
                        if (article.id) {
                          await adminSupportContentApi.archiveArticle(
                            article.id,
                          );

                          const updated = [...categories];
                          updated[catIndex].articles = updated[
                            catIndex
                          ].articles.filter((item) => item.id !== article.id);
                          setCategories(updated);
                        } else {
                          const updated = [...categories];
                          updated[catIndex].articles.splice(artIndex, 1);
                          setCategories(updated);
                        }
                      } catch (err) {
                        console.error("Failed to delete support article", err);
                        alert("Failed to delete support article.");
                      }
                    }}
                    sx={{ mt: 2 }}
                  >
                    🗑 Delete Article
                  </Button>
                </AccordionDetails>
              </Accordion>
            ))}

            <Button
              variant="outlined"
              onClick={async () => {
                if (!category.id) {
                  alert("Please save the category before adding articles.");
                  return;
                }

                try {
                  const newArticle = await adminSupportContentApi.createArticle(
                    {
                      supportCategoryId: category.id,
                      title: "New Article",
                      slug: `new-article-${Date.now()}`,
                      content: "",
                      sortOrder: category.articles.length + 1,
                      published: true,
                    },
                  );

                  const updated = [...categories];
                  updated[catIndex].articles = [
                    ...updated[catIndex].articles,
                    newArticle,
                  ];

                  setCategories(updated);
                } catch (err) {
                  console.error("Failed to create support article", err);
                  alert("Failed to create support article.");
                }
              }}
              sx={{ mt: 2 }}
            >
              ➕ New Article
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}
    </Container>
  );
};

export default SupportAdminPage;
